import React, { useRef, useState } from 'react'
import { Box, Button, Typography } from '@mui/material'
import { useReactToPrint } from 'react-to-print'
import { pdsAnalytics } from 'analytics/pdsAnalytics'
import { useTranslation } from 'react-i18next'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import AcceptSpinner from 'components/AcceptSpinner'
import { IS_NEW_LAYOUT_ENABLED } from 'constants/layoutConstant'

const printClassName = 'pds__print'
const downloadClassName = 'pds__dowload'
const printStyling =
    'html,body {  zoom: 85%; height: initial !important;overflow: initial !important;-webkit-print-color-adjust: exact;}@page {size: auto;margin: 20mm 2mm;}'

const AcceptFooter = () => {
    const { t } = useTranslation()
    const nodeRef = useRef(null)
    const containerRef = useRef(null)
    const [isLoading, setIsLoading] = useState(false)

    const handlePrint = useReactToPrint({
        content: () => {
            const mainElement = document.getElementsByClassName('pds__container')[0].cloneNode(true)
            mainElement.classList.add(printClassName)
            return mainElement
        },
        onBeforeGetContent: () => {
            pdsAnalytics.track('print')
        },
        documentTitle: 'pds-summary',
        pageStyle: printStyling
    })

    const handleDownload = () => {
        pdsAnalytics.track('download')
        const mainElement = document.getElementsByClassName('pds__container')[0]
        mainElement.classList.add(downloadClassName)
        setIsLoading(true)

        html2canvas(mainElement, {
            scrollY: -window.scrollY,
            useCORS: true,
            dpi: 300
        }).then((canvas) => {
            const imgData = canvas.toDataURL('image/png')
            const imgWidth = 210
            const imgHeight = (canvas.height * imgWidth) / canvas.width

            // Convert the height from pixels to millimeters
            const pageHeight = imgHeight + 20

            const doc = new jsPDF({
                orientation: 'p',
                unit: 'mm',
                format: [imgWidth, pageHeight]
            })

            doc.addImage(imgData, 'PNG', 0, 10, imgWidth, imgHeight)
            doc.save('pds-summary.pdf')
            mainElement.classList.remove(downloadClassName)
            setIsLoading(false)
        })
    }

    return (
        <>
            {isLoading && IS_NEW_LAYOUT_ENABLED && <AcceptSpinner />}

            <Box
                data-testid="Accept-footer-container"
                component="footer"
                position="sticky"
                ref={nodeRef}
                sx={{
                    backgroundColor: 'background.default',
                    borderTop: (theme) => `1px solid ${theme.palette.common.gray.dark}`,
                    boxShadow: { xs: '0px 0px 10px 0px rgba(0, 0, 0, 0.03)', md: 'none' },
                    bottom: 0,
                    p: {
                        xs: '0px 12px',
                        smd: '0px'
                    },
                    transition: '.3s all',
                    overflow: 'hidden',
                    zIndex: 2
                }}
            >
                <Box
                    data-testid="Accept-footer-section"
                    sx={{
                        display: { xs: 'flex', smd: 'grid' },
                        flexDirection: { xs: 'column', smd: 'row' },
                        alignItems: 'center'
                    }}
                >
                    <Box
                        ref={containerRef}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            flexDirection: { xs: 'column', smd: 'row' },
                            gap: 4,
                            order: 2,
                            textAlign: 'center',
                            py: 4,
                            pr: { xs: 0, smd: 7, md: 14 }
                        }}
                    >
                        <Typography
                            color="common.charcoal.main"
                            data-testid="footer-text"
                            sx={{
                                typography: { xs: 'boldBody1', smd: 'boldSubTitle' },
                                letterSpacing: 0
                            }}
                        >
                            {t('save_print')}
                        </Typography>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'stretch',
                                flexDirection: 'row',
                                justifyContent: 'center',
                                flexWrap: 'wrap',
                                gap: 4,
                                width: { xs: '100%', smd: 'initial' }
                            }}
                        >
                            <Button
                                variant="outlined"
                                disableRipple
                                onClick={handlePrint}
                                data-testid="print icon"
                                sx={{
                                    p: '12px 14px',
                                    height: '40px',
                                    minWidth: { xs: '150px', sm: '155px', smd: '86px' }
                                }}
                            >
                                {t('print')}
                            </Button>
                            <Button
                                variant="contained"
                                disableRipple
                                onClick={handleDownload}
                                data-testid="download icon"
                                sx={{
                                    p: '12px 14px',
                                    height: '40px',
                                    minWidth: { xs: '150px', sm: '155px', smd: '125px' },
                                    backgroundColor: 'common.link.main'
                                }}
                            >
                                {t('download')}
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </>
    )
}

export default AcceptFooter
